import posthog from 'posthog-js';

export type PaywallSurface =
  | 'radar'
  | 'heatmap'
  | 'hub'
  | 'stock'
  | 'portfolios'
  | 'portfolio_detail'
  | 'subscription_modal'
  | 'watchlist'
  | 'quick_add';

export type PaywallTrigger =
  | 'inline_overlay'
  | 'modal'
  | 'metric_gate'
  | 'list_gate'
  | 'combine_gate'
  | 'view_limit' // legacy
  | 'ratings_gate'
  | 'charts_gate'
  | 'price_gate'
  | 'scroll_gate'; // anonymous stock page: register modal opened on scroll

export interface PaywallContext {
  surface: PaywallSurface;
  trigger: PaywallTrigger;
  page?: string;
  list_id?: string;
  metric?: string;
  portfolio_id?: string;
  ticker?: string;
  is_authenticated?: boolean;
}

function currentPage(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.location.pathname;
}

function withPage(ctx: PaywallContext): PaywallContext {
  return {
    ...ctx,
    page: ctx.page ?? currentPage(),
  };
}

/** Inline paywall or upgrade card shown to the user. */
export function trackPaywallViewed(ctx: PaywallContext) {
  posthog.capture('paywall_viewed', withPage(ctx));
}

/** User clicked a primary upgrade CTA (Start Premium). */
export function trackPaywallCtaClicked(ctx: PaywallContext & { billing_cycle: 'monthly' | 'yearly' }) {
  const payload = withPage(ctx);
  posthog.capture('paywall_cta_clicked', payload);
  posthog.capture('checkout_initiated', {
    billing_cycle: ctx.billing_cycle,
    plan: 'premium',
    source: ctx.surface,
    trigger: ctx.trigger,
    page: payload.page,
    list_id: ctx.list_id,
    metric: ctx.metric,
    portfolio_id: ctx.portfolio_id,
    ticker: ctx.ticker,
  });
}

/** User attempted to use a premium-gated feature before seeing the full paywall. */
export function trackPremiumFeatureBlocked(ctx: PaywallContext & { action: string }) {
  posthog.capture('premium_feature_blocked', withPage(ctx));
}

/** Subscription modal opened (explicit modal, not inline overlay). */
export function trackSubscriptionModalViewed(ctx: Omit<PaywallContext, 'trigger'> & { trigger?: PaywallTrigger }) {
  posthog.capture('subscription_modal_viewed', withPage({
    ...ctx,
    trigger: ctx.trigger ?? 'modal',
  }));
}
